import type { SupportedLocale } from '@brightdrop/localization-core';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '~/app/store';
import useStoredLocale from '~/common/hooks/useStoredLocale';
import { getStoredLocale } from '~/common/utils/signed-out-locale.utils';
import { setCurrentLocale } from '~/store/profile/profileSlice';
import {
  selectCurrentLocale,
  selectProfile,
} from '~/store/profile/profileSlice.selectors';

/**
 * The language a visitor picks on the login screen. The choice is stored, so
 * it carries through account creation and into business enrollment, and it is
 * applied to the app language so the login screen itself switches. Once a
 * profile has loaded, its language decides instead.
 * @param isEnabled - Whether the visitor's choice is applied to the app language
 * @returns The selected locale and a setter that stores and applies it
 */
const useLoginLocale = (
  isEnabled: boolean
): [SupportedLocale, (locale: SupportedLocale) => void] => {
  // TODO: Like Quick Pay, this writes the profile slice's `currentLocale`
  // because that is what the app syncs to i18n and the request headers. Move
  // it to the localization core once that owns the active locale.
  const dispatch = useAppDispatch();
  const hasProfile = Boolean(useSelector(selectProfile));
  const currentLocale = useSelector(selectCurrentLocale);
  const isApplied = isEnabled && !hasProfile;

  const [locale, setStoredLocale] = useStoredLocale();

  // Applies the stored locale on arrival and again if something else sets the
  // app locale afterwards, such as Quick Pay restoring its arrival language or
  // the app shell's own startup effect, which runs after this one.
  useEffect(() => {
    if (!isApplied) return;

    const stored = getStoredLocale();
    if (stored && stored !== currentLocale) {
      dispatch(setCurrentLocale(stored));
    }
  }, [currentLocale, dispatch, isApplied]);

  const updateLocale = useCallback(
    (next: SupportedLocale) => {
      setStoredLocale(next);
      if (isApplied) {
        dispatch(setCurrentLocale(next));
      }
    },
    [dispatch, isApplied, setStoredLocale]
  );

  return [locale, updateLocale];
};

export default useLoginLocale;
